import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useV2WebCall, type V2CallEvent } from "@/hooks/useV2WebCall";
import VariablesForm, {
  filledVariables,
} from "@/components/pages/public-test/VariablesForm";
import {
  getPublicTestLinkInfo,
  PublicTestLinkError,
  startPublicTestSession,
  type PublicTestLinkInfo,
} from "@/services/publicTestLinkService";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  AlertCircle,
  Clock,
  Loader2,
  Mic,
  MicOff,
  PhoneOff,
  Play,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

const GENERIC_ERROR =
  "Something went wrong. Please try again in a moment, or contact the person who shared this link.";

/**
 * What a visitor may read. The API's own messages for an unknown, expired or revoked link and
 * for too many attempts (404, 410, 429) are written for visitors; anything else - server
 * errors, network failures - is replaced by a fixed message and left to the console.
 */
function publicMessage(error: unknown): string {
  if (
    error instanceof PublicTestLinkError &&
    [404, 410, 429].includes(error.status)
  ) {
    return error.message;
  }
  return GENERIC_ERROR;
}

/** Same call-log contract as the in-app Test Agent: the row must not sit at "queued". */
async function endCallOnServer(callId: string, reason: string) {
  try {
    await fetch(
      `${import.meta.env.VITE_CALL_SERVICE_URL || ""}/api/call/${callId}/end`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ended_reason:
            reason === "agent-left"
              ? "assistant-ended-web-session"
              : "customer-ended-web-session",
        }),
      },
    );
  } catch (error) {
    console.error("Failed to mark call as ended:", error);
  }
}

type OrbState = "idle" | "connecting" | "listening" | "speaking" | "muted";

/**
 * The page's one moving element. Slow and soft: it floats when idle, breathes while
 * connecting, pulses gently when listening and swells while the assistant talks.
 */
function Orb({ state, compact = false }: { state: OrbState; compact?: boolean }) {
  const reduce = useReducedMotion();

  const scale =
    state === "speaking"
      ? [1, 1.14, 1.04, 1.12, 1]
      : state === "listening"
      ? [1, 1.04, 1]
      : state === "connecting"
      ? [0.94, 1.02, 0.94]
      : [1, 1.02, 1];
  const duration =
    state === "speaking" ? 1.6 : state === "connecting" ? 2.4 : 4;

  return (
    // compact: smaller on phones so a long form below it stays near the top of the screen
    <div
      className={`relative flex items-center justify-center sm:h-64 sm:w-64 ${
        compact ? "h-28 w-28" : "h-56 w-56"
      }`}
    >
      {/* Soft halo */}
      <motion.div
        aria-hidden
        className="absolute inset-0 rounded-full bg-primary/15 blur-3xl"
        animate={
          reduce
            ? undefined
            : { opacity: state === "speaking" ? [0.7, 1, 0.7] : [0.4, 0.6, 0.4] }
        }
        transition={{ duration, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Ripple while the assistant speaks */}
      <AnimatePresence>
        {state === "speaking" && !reduce && (
          <motion.div
            aria-hidden
            className="absolute h-40 w-40 rounded-full border border-primary/40"
            initial={{ scale: 1, opacity: 0.6 }}
            animate={{ scale: 1.7, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
          />
        )}
      </AnimatePresence>
      {/* Core */}
      <motion.div
        aria-hidden
        className={`rounded-full bg-gradient-to-br from-primary/90 via-primary/60 to-primary/25 shadow-[0_0_60px_-10px] shadow-primary/50 sm:h-40 sm:w-40 ${
          compact ? "h-20 w-20" : "h-40 w-40"
        }`}
        animate={
          reduce
            ? undefined
            : { scale, opacity: state === "muted" ? 0.45 : 1 }
        }
        transition={{ duration, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
}

function formatDuration(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Live session panel. Mounted only once a session exists, so it starts the audio on mount. */
function Session({
  wsUrl,
  callId,
  onEnded,
  onRetry,
}: {
  wsUrl: string;
  callId: string;
  /** The call ran and finished. */
  onEnded: () => void;
  /** The call never started: back to the start screen without the "ended" wording. */
  onRetry: () => void;
}) {
  const {
    start,
    disconnect,
    toggleMic,
    setOnClosed,
    connected,
    micEnabled,
    agentSpeaking,
  } = useV2WebCall();
  // Visitors only ever see a fixed message; the real cause goes to the console.
  const [failed, setFailedState] = useState(false);
  const failedRef = useRef(false);
  const setFailed = (value: boolean) => {
    failedRef.current = value;
    setFailedState(value);
  };
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    // StrictMode mounts, unmounts and remounts; a cancellable timer keeps the throwaway
    // pass from opening a socket (same approach as the in-app V2 test panel).
    let started = false;
    const timer = setTimeout(() => {
      started = true;
      setOnClosed((reason) => {
        void endCallOnServer(callId, reason);
        // A call that failed to start is not an "ended" call; Try again handles that screen.
        if (!failedRef.current) onEnded();
      });
      start({
        wsUrl,
        onEvent: (event: V2CallEvent) => {
          if (event.type === "Error") {
            console.error("Test session error:", event.description);
            setFailed(true);
          }
        },
      }).catch((err: any) => {
        console.error("Test session failed to start:", err);
        void endCallOnServer(callId, "error");
        setFailed(true);
      });
    }, 300);

    return () => {
      clearTimeout(timer);
      if (started) disconnect("unmount");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!connected) return;
    const tick = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(tick);
  }, [connected]);

  if (failed) {
    return (
      <div className="flex flex-col items-center gap-5 text-center">
        <AlertCircle className="h-8 w-8 text-muted-foreground" />
        <p className="max-w-xs text-muted-foreground">
          We couldn't start the call. Please check that your microphone is
          allowed and try again.
        </p>
        <Button onClick={onRetry}>Try again</Button>
      </div>
    );
  }

  const orbState: OrbState = !connected
    ? "connecting"
    : !micEnabled
    ? "muted"
    : agentSpeaking
    ? "speaking"
    : "listening";
  const caption = {
    idle: "",
    connecting: "Connecting…",
    listening: "Listening — go ahead and speak",
    speaking: "Speaking…",
    muted: "Your microphone is muted",
  }[orbState];

  return (
    <div className="flex flex-col items-center gap-8">
      <Orb state={orbState} />
      <div className="text-center">
        <p className="h-6 text-muted-foreground" aria-live="polite">
          {caption}
        </p>
        <p className="mt-1 font-mono-data text-sm text-muted-foreground/70">
          {formatDuration(seconds)}
        </p>
      </div>
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          className="h-12 w-12 rounded-full"
          onClick={toggleMic}
          aria-label={micEnabled ? "Mute microphone" : "Unmute microphone"}
        >
          {micEnabled ? (
            <Mic className="h-5 w-5" />
          ) : (
            <MicOff className="h-5 w-5" />
          )}
        </Button>
        <Button
          variant="destructive"
          size="icon"
          className="h-12 w-12 rounded-full"
          onClick={() => disconnect("user")}
          aria-label="End call"
        >
          <PhoneOff className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Public page behind /test/:token. Needs no Voxi login: the token in the URL is the
 * credential, and the API refuses it once the link expires or is revoked.
 */
export default function PublicTestAgentPage() {
  const { token = "" } = useParams();
  const [info, setInfo] = useState<PublicTestLinkInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [session, setSession] = useState<{ wsUrl: string; callId: string } | null>(
    null,
  );
  const [ended, setEnded] = useState(false);
  // Stops a double click on Start from opening two billable sessions.
  const startingRef = useRef(false);

  useEffect(() => {
    getPublicTestLinkInfo(token)
      .then(setInfo)
      .catch((error: Error) => {
        console.error("Failed to load test link:", error);
        setLoadError(
          error instanceof PublicTestLinkError && error.status === 404
            ? "This test link is not valid."
            : publicMessage(error),
        );
      });
  }, [token]);

  const begin = async () => {
    if (startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    setStartError(null);
    try {
      const result = await startPublicTestSession(
        token,
        name.trim() || undefined,
        filledVariables(values),
      );
      setEnded(false);
      setSession({ wsUrl: result.v2_ws_url, callId: result.call_id });
    } catch (error: any) {
      console.error("Failed to start test session:", error);
      setStartError(publicMessage(error));
    } finally {
      startingRef.current = false;
      setStarting(false);
    }
  };

  // One key per screen so AnimatePresence cross-fades between them.
  const screen = loadError ? "error" : !info ? "loading" : session ? "live" : "start";
  // The start screen is wider when there is a form to fit; everything else stays narrow.
  const wide = screen === "start" && (info?.variables?.length ?? 0) > 0;

  return (
    <div className="relative flex min-h-dvh flex-col overflow-hidden bg-background text-foreground">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-96 bg-gradient-to-b from-primary/10 to-transparent"
      />

      <header className="relative z-10 px-6 py-5">
        <span className="font-display text-2xl tracking-tight">Voxi</span>
      </header>

      <main className="relative z-10 flex flex-1 items-center justify-center px-6 pb-16">
        <AnimatePresence mode="wait">
          <motion.div
            key={screen}
            className={`w-full text-center ${wide ? "max-w-2xl" : "max-w-md"}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {screen === "error" && (
              <div className="flex flex-col items-center gap-4">
                <AlertCircle className="h-8 w-8 text-muted-foreground" />
                <p className="max-w-xs text-muted-foreground">{loadError}</p>
              </div>
            )}

            {screen === "loading" && (
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
            )}

            {screen === "live" && session && (
              <Session
                wsUrl={session.wsUrl}
                callId={session.callId}
                onEnded={() => {
                  setSession(null);
                  setEnded(true);
                }}
                onRetry={() => setSession(null)}
              />
            )}

            {screen === "start" && info && (
              <div className="flex flex-col items-center gap-8">
                <Orb state="idle" compact={wide} />
                <div className="space-y-2">
                  <h1 className="break-words font-display text-3xl tracking-tight sm:text-4xl">
                    {ended ? "Call ended" : info.assistant_name}
                  </h1>
                  <p className="text-muted-foreground">
                    {ended
                      ? `Thanks for trying ${info.assistant_name}. Start another conversation anytime.`
                      : `Have a conversation with ${info.assistant_name}, right in your browser.`}
                  </p>
                </div>

                <VariablesForm
                  names={info.variables ?? []}
                  values={values}
                  onChange={(variable, value) =>
                    setValues((prev) => ({ ...prev, [variable]: value }))
                  }
                  onClear={() => setValues({})}
                />

                <div className="w-full max-w-md space-y-3">
                  <Input
                    placeholder="Your name (optional)"
                    value={name}
                    maxLength={80}
                    className="h-11 text-center"
                    onChange={(e) => setName(e.target.value)}
                  />
                  <Button
                    size="lg"
                    className="h-12 w-full rounded-full text-base"
                    onClick={begin}
                    disabled={starting}
                  >
                    {starting ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <Play className="mr-2 h-4 w-4" />
                        {ended ? "Start again" : "Start conversation"}
                      </>
                    )}
                  </Button>
                  {startError && (
                    <p className="text-sm text-destructive">{startError}</p>
                  )}
                </div>

                <p className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  Link valid until {new Date(info.expires_at).toLocaleString()}
                </p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
